import * as React from 'react'
import { useMutationState } from '@tanstack/react-query'
import { Cookies, useCookies } from 'react-cookie'
import { CurrencyCode } from '@repo/graphql-types'
import { useUpdateUserCurrencyMutation } from 'codegen/generated/user-preferences'
import { CURRENCY_COOKIE_NAME } from 'constants/cookies'
import { getApexCookieDomain, getCookieDomainCandidates } from 'utils/cookie-domain'
import { sessionStore } from 'utils/storage'
import { useUserQuery } from './auth'
import { isLoggedInQuerySelector } from '../selectors'

const RELOAD_STORAGE_KEY = 'cf-currency-reload-pending'

export interface UseSyncCurrencyCookieOptions {
  /** Runs after the cookie was updated to match the account currency. */
  onSync?: () => void
}

export function useSyncCurrencyCookie(options: UseSyncCurrencyCookieOptions = {}) {
  const { onSync = () => window.location.reload() } = options
  const {
    data: accountCurrency,
    isSuccess,
    isFetching,
  } = useUserQuery(undefined, {
    select: data =>
      !data.me?.errors?.length && isLoggedInQuerySelector(data)
        ? data.me?.user?.currency.code
        : undefined,
  })
  const [cookies, setCookie, removeCookie] = useCookies([CURRENCY_COOKIE_NAME])
  const currency = cookies[CURRENCY_COOKIE_NAME]
  const currencyUpdates = useMutationState({
    filters: { mutationKey: useUpdateUserCurrencyMutation.getKey() },
    select: mutation => mutation.state.status,
  })
  const isChangingCurrency = currencyUpdates.some(
    status => status === 'pending' || status === 'success',
  )
  const hasReconciled = React.useRef(false)

  React.useEffect(() => {
    // Currency selectors reload after saving; their cached user response may still have the old currency.
    if (
      !isSuccess ||
      isFetching ||
      isChangingCurrency ||
      hasReconciled.current ||
      !accountCurrency
    ) {
      return
    }

    if (!Object.values(CurrencyCode).includes(accountCurrency)) {
      return
    }

    if (typeof currency === 'string' && currency.toLowerCase() === accountCurrency.toLowerCase()) {
      hasReconciled.current = true
      sessionStore.remove(RELOAD_STORAGE_KEY)
      return
    }

    const { hostname } = window.location
    for (const domain of getCookieDomainCandidates(hostname)) {
      removeCookie(CURRENCY_COOKIE_NAME, { path: '/', domain })
    }
    // WordPress can leave narrower cookies that shadow the shared-domain value.
    removeCookie(CURRENCY_COOKIE_NAME, { path: '/' })
    removeCookie(CURRENCY_COOKIE_NAME, { path: '/', domain: 'staging.creativefabrica.com' })

    const value = accountCurrency.toUpperCase()
    const cookieOptions = { path: '/', maxAge: 60 * 60 * 24 * 365 } as const
    const apexDomain = getApexCookieDomain(hostname)
    if (apexDomain) {
      setCookie(CURRENCY_COOKIE_NAME, value, { ...cookieOptions, domain: apexDomain })
    }
    if (new Cookies().get(CURRENCY_COOKIE_NAME) !== value) {
      setCookie(CURRENCY_COOKIE_NAME, value, cookieOptions)
    }

    if (new Cookies().get(CURRENCY_COOKIE_NAME) !== value) {
      hasReconciled.current = true
      return
    }

    hasReconciled.current = true

    // This marker survives reloads until a page load confirms the currencies agree.
    if (sessionStore.getString(RELOAD_STORAGE_KEY)) {
      return
    }
    if (!sessionStore.setString(RELOAD_STORAGE_KEY, '1')) {
      return
    }

    onSync()
  }, [
    accountCurrency,
    currency,
    isSuccess,
    isFetching,
    isChangingCurrency,
    onSync,
    setCookie,
    removeCookie,
  ])
}
